import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * Single Row Selector Template
 * Based on Figma design: https://www.figma.com/design/Nhga9ODmaPGwSQJjO8lQZi/Calibrate-Mate?node-id=300-8008&m=dev
 * Example: Skills Confirmation
 * 
 * Design specs from Figma:
 * - Chat bubble with 10px border radius (10px 10px 10px 0px)
 * - Chips with #E1EFFF background, #0F42BC text, 16px border radius
 * - Check circle icons with Material Icons Round font
 * - Button with #4599FA background, 8px border radius
 */
const SingleRowSelectorTemplate = ({
  title = "Got it! Just to confirm — you're looking for candidates with the following skills, right? 👇",
  items = [],
  selectedItems = [],
  onConfirmed,
  onAddMore,
  confirmButtonText = "Confirm Skills",
  addMoreButtonText = "Add more",
  readOnly = false,
  showAddMore = true
}) => {
  const [localSelectedItems, setLocalSelectedItems] = useState(selectedItems);

  // Update local state when selectedItems prop changes
  useEffect(() => {
    // Only update if the arrays are actually different
    if (JSON.stringify(localSelectedItems) !== JSON.stringify(selectedItems)) {
      setLocalSelectedItems(selectedItems);
    }
  }, [selectedItems, localSelectedItems]);

  const handleItemToggle = (item) => {
    if (readOnly) return;
    
    setLocalSelectedItems(prev => {
      if (prev.includes(item)) {
        return prev.filter(i => i !== item);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleConfirm = () => {
    console.log('🔍 SKILLS_TEMPLATE: handleConfirm called with localSelectedItems:', localSelectedItems);
    if (onConfirmed) {
      // Return structured response like other templates for proper template_props preservation
      const confirmationData = {
        selections: localSelectedItems,
        template_result: localSelectedItems
      };
      console.log('🎯 SKILLS_TEMPLATE: Calling onConfirmed with:', confirmationData);
      onConfirmed(confirmationData);
    } else {
      console.warn('❌ SKILLS_TEMPLATE: onConfirmed callback is not available');
    }
  };

  const handleAddMore = () => {
    if (onAddMore) {
      onAddMore();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        padding: '0px 8px', // Match Figma chat bubble padding
        backgroundColor: 'transparent',
      }}
    >
      {/* Chat Bubble Container */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px 10px 10px 0px', // Exact Figma spec
          padding: '0px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* Title Text */}

        {/* Items Row */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            alignSelf: 'stretch',
            marginBottom: '8px',
          }}
        >
          {items.map((item, index) => {
            // ✅ Fix: Ensure itemLabel is always a string
            let itemLabel;
            if (typeof item === 'object' && item !== null) {
              // If item is an object, try to get label, name, or convert to string
              itemLabel = item.label || item.name || item.value || String(item);
            } else {
              // If item is already a string or primitive, use it directly
              itemLabel = String(item);
            }

            return (
              <Chip
                key={index}
                icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                label={itemLabel}
                onClick={() => handleItemToggle(item)}
                sx={{
                  backgroundColor: '#E1EFFF', // Exact Figma color
                  color: '#0F42BC', // Exact Figma color
                  borderRadius: '16px',
                  padding: '4px 12px 4px 10px', // Exact Figma padding
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '1.4285714285714286em',
                  cursor: readOnly ? 'default' : 'pointer',
                  border: 'none',
                  '& .MuiChip-icon': {
                    color: '#0F42BC',
                    fontSize: '14px',
                    fontFamily: 'Material Icons Round',
                  },
                  '&:hover': readOnly ? {} : {
                    backgroundColor: '#D1E7FF',
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Confirm Button */}
        {!readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={localSelectedItems.length === 0}
              sx={{
                backgroundColor: '#4599FA', // Exact Figma color
                color: '#FFFFFF',
                fontFamily: 'Roboto',
                fontSize: '14px',
                fontWeight: 400,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                height: '36px', // Exact Figma height
                '&:hover': {
                  backgroundColor: '#3b82d6',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#9CA3AF',
                }
              }}
            >
              {confirmButtonText}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SingleRowSelectorTemplate;
